import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SharedService } from 'src/app/shared/services/shared.service';
import { NetworkSyncService } from 'src/app/shared/services/network-sync.service';
import { LocalDatabaseService, CalendarEventEntry } from 'src/app/shared/services/local-database.service';
import { SmsService } from 'src/app/services/sms.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private readonly API = 'http://localhost:3000/api/calendar_events';

  constructor(
    private http: HttpClient,
    private sharedService: SharedService,
    private networkSync: NetworkSyncService,
    private localDb: LocalDatabaseService,
    private smsService: SmsService
  ) {}

  /** Send an SMS calendar invitation to a customer */
  async sendInvitation(event: CalendarEventEntry, customerPhone: string): Promise<void> {
    if (!customerPhone) return;

    const eventDate = new Date(event.start);
    const endDate = new Date(event.end);
    const dateStr = eventDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const companyName = this.sharedService.company_info?.company_name || 'Our office';

    // Build a shortened Google Calendar URL to stay under Twilio's 160-char trial limit
    const formatGcal = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(event.title)}` +
      `&dates=${formatGcal(eventDate)}/${formatGcal(endDate)}`;

    const message = `Appt: ${event.title}\n` +
      `Date: ${eventDate.toLocaleDateString()} @ ${timeStr}\n\n` +
      `Add to Calendar: ${gcalUrl}`;

    try {
      await firstValueFrom(this.smsService.sendSms(customerPhone, message));
    } catch (error) {
      console.error('[CalendarService] Failed to send SMS invitation:', error);
      throw error;
    }
  }

  private get companyId(): string {
    return this.sharedService.company_info?.company_id || '';
  }

  /** Load all events for the company. Falls back to IndexedDB when offline. */
  async getEvents(preferCache = false): Promise<CalendarEventEntry[]> {
    if (preferCache) {
      return this.localDb.calendarEvents.where('company_id').equals(this.companyId).toArray();
    }
    try {
      const params = new HttpParams().set('company_id', this.companyId);
      const res = await firstValueFrom(
        this.http.get<{ message: string; calendar_events: any[] }>(this.API, { params })
      );
      const events: CalendarEventEntry[] = res.calendar_events.map(e => ({
        id: e._id,
        company_id: e.company_id,
        title: e.title,
        start: e.start,
        end: e.end,
        customer_id: e.customer_id,
        description: e.description,
        send_reminder: e.send_reminder,
        created_by: e.created_by,
        updatedAt: e.updatedAt
      }));
      // Cache locally
      await this.localDb.calendarEvents.where('company_id').equals(this.companyId).delete();
      await this.localDb.calendarEvents.bulkPut(events);
      return events;
    } catch {
      console.warn('[CalendarService] Offline — loading from IndexedDB');
      return this.localDb.calendarEvents.where('company_id').equals(this.companyId).toArray();
    }
  }

  /** Add a new event. Queues the POST if offline. */
  async addEvent(event: Omit<CalendarEventEntry, 'id' | 'company_id'>): Promise<CalendarEventEntry> {
    const tempId = uuidv4();
    const newEvent: CalendarEventEntry = {
      id: tempId,
      company_id: this.companyId,
      ...event
    };

    // Optimistic local write
    await this.localDb.calendarEvents.put(newEvent);

    // Queue for server sync
    await this.networkSync.queueOperation('POST', this.API, {
      ...newEvent,
      company_id: this.companyId
    });

    // If online, refresh from server to get the real _id
    if (this.networkSync.isOnline) {
      const fresh = await this.getEvents();
      return fresh.find(e => e.title === newEvent.title && e.start === newEvent.start) ?? newEvent;
    }
    return newEvent;
  }

  /** Update an existing event by its server _id. Queues PUT if offline. */
  async updateEvent(id: string, event: Partial<CalendarEventEntry>): Promise<void> {
    const updated = { ...event, updatedAt: new Date().toISOString() };

    // Optimistic local update
    await this.localDb.calendarEvents.update(id, updated);

    // Queue for server sync
    await this.networkSync.queueOperation('PUT', `${this.API}/${id}`, {
      company_id: this.companyId,
      ...event
    });
  }

  /** Delete an event by its server _id. Queues DELETE if offline. */
  async deleteEvent(id: string): Promise<void> {
    await this.localDb.calendarEvents.delete(id);
    await this.networkSync.queueOperation('DELETE', `${this.API}/${id}`);
  }
}
